/**
 * PWA Helper Functions
 * Utilities for detecting PWA installation status and managing installation prompts
 */

/**
 * Check if the device is mobile
 */
export const isMobileDevice = () => {
  // Check multiple indicators for mobile devices
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Check for mobile user agents
  const mobileRegex =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  const isMobileUA = mobileRegex.test(userAgent.toLowerCase());

  // Check for touch support
  const hasTouchScreen =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  // Check screen size
  const isSmallScreen = window.innerWidth <= 768;

  return isMobileUA || (hasTouchScreen && isSmallScreen);
};

/**
 * Detect if the app is running as a PWA (installed)
 */
export const isPWAInstalled = () => {
  // Method 1: Check display mode
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;

  // Method 2: Check navigator.standalone (iOS Safari)
  const isIOSStandalone = window.navigator.standalone === true;

  // Method 3: Check referrer (Android)
  const isAndroidPWA = document.referrer.includes("android-app://");

  // Method 4: Check display mode via media query for other modes
  const isFullscreen = window.matchMedia("(display-mode: fullscreen)").matches;
  const isMinimalUI = window.matchMedia("(display-mode: minimal-ui)").matches;

  return (
    isStandalone ||
    isIOSStandalone ||
    isAndroidPWA ||
    isFullscreen ||
    isMinimalUI
  );
};

/**
 * Check if the browser supports PWA installation
 */
export const canInstallPWA = () => {
  // Check if beforeinstallprompt is supported (Chrome, Edge, Samsung Internet)
  const hasBeforeInstallPrompt = "BeforeInstallPromptEvent" in window;

  // For iOS, check if it's Safari
  const isIOSSafari =
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !window.MSStream &&
    /Safari/.test(navigator.userAgent);

  return hasBeforeInstallPrompt || isIOSSafari;
};

/**
 * Check if the PWA is installed using getInstalledRelatedApps API
 */
export const checkInstalledPWA = async () => {
  if ("getInstalledRelatedApps" in navigator) {
    try {
      const relatedApps = await navigator.getInstalledRelatedApps();
      return relatedApps.length > 0;
    } catch (error) {
      console.warn("Error checking installed apps:", error);
    }
  }
  return false;
};

/**
 * Try to open the installed PWA
 */
export const openInstalledPWA = () => {
  // For mobile devices, try to open the app
  const platform = getPlatform();

  if (platform === "android") {
    // On Android, we can try to use the app's URL scheme
    // This will open the PWA if it's installed
    const appUrl = window.location.origin;

    // Create a temporary link and click it
    const link = document.createElement("a");
    link.href = appUrl;
    link.target = "_blank";

    // For Android PWAs, this might open in the installed app
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  } else if (platform === "ios") {
    // For iOS, we need to guide users to open from home screen
    // iOS doesn't allow programmatic opening of PWAs
    return false;
  } else {
    // For desktop, open in a new window
    window.open(window.location.origin, "_blank");
    return true;
  }
};

/**
 * Get the current browser/platform
 */
export const getPlatform = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(userAgent)) {
    return "ios";
  } else if (/android/.test(userAgent)) {
    return "android";
  } else if (/windows phone/.test(userAgent)) {
    return "windows-phone";
  } else {
    return "desktop";
  }
};

/**
 * Store the beforeinstallprompt event
 */
let deferredInstallPrompt = null;

export const setDeferredInstallPrompt = (event) => {
  deferredInstallPrompt = event;
};

export const getDeferredInstallPrompt = () => {
  return deferredInstallPrompt;
};

/**
 * Clear the deferred install prompt after use
 */
export const clearDeferredInstallPrompt = () => {
  deferredInstallPrompt = null;
};

/**
 * Show the PWA install prompt
 */
export const showPWAInstallPrompt = async () => {
  const platform = getPlatform();

  if (platform === "ios") {
    // iOS doesn't support programmatic installation
    // Return instructions for manual installation
    return {
      success: false,
      platform: "ios",
      instructions: {
        title: "Install App on iOS",
        steps: [
          "Tap the Share button (box with arrow)",
          'Scroll down and tap "Add to Home Screen"',
          'Tap "Add" to install the app',
          "Open the app from your home screen to enable notifications",
        ],
      },
    };
  } else if (deferredInstallPrompt) {
    // Chrome, Edge, Samsung Internet
    try {
      // Show the install prompt
      deferredInstallPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await deferredInstallPrompt.userChoice;

      // Clear the deferred prompt
      clearDeferredInstallPrompt();

      return {
        success: outcome === "accepted",
        platform: "android",
        outcome,
      };
    } catch (error) {
      console.error("Error showing install prompt:", error);
      return {
        success: false,
        platform: "android",
        error: error.message,
      };
    }
  } else {
    // No install prompt available
    return {
      success: false,
      platform: platform,
      error: "Install prompt not available",
    };
  }
};

/**
 * Listen for PWA installation state changes
 */
export const onPWAInstallStateChange = (callback) => {
  // Listen for successful installation
  window.addEventListener("appinstalled", (event) => {
    console.info("PWA was installed successfully");
    callback({ installed: true, event });
  });

  // Listen for display mode changes
  const checkDisplayMode = () => {
    if (isPWAInstalled()) {
      callback({ installed: true });
    }
  };

  // Check periodically for iOS where events might not fire
  if (getPlatform() === "ios") {
    const intervalId = setInterval(() => {
      if (isPWAInstalled()) {
        clearInterval(intervalId);
        callback({ installed: true });
      }
    }, 1000);

    // Clear interval after 30 seconds to prevent memory leak
    setTimeout(() => clearInterval(intervalId), 30000);
  }

  // Listen for visibility changes (user might have installed and returned)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      checkDisplayMode();
    }
  });
};

/**
 * Initialize PWA install prompt listener
 */
export const initializePWAInstallPrompt = () => {
  // Capture the beforeinstallprompt event
  window.addEventListener("beforeinstallprompt", (event) => {
    // Prevent the default prompt
    event.preventDefault();

    // Store the event for later use
    setDeferredInstallPrompt(event);

    console.info("PWA install prompt ready");
  });
};
